import { Button, Checkbox, TableGrid, TableGridColumn } from "@jugl-web/ui-kit";
import { useTranslations } from "@jugl-web/utils";
import { InvoiceTaxRow } from "@web-src/modules/invoices/types";
import { useState } from "react";
import { ReactComponent as AddIcon } from "./assets/add.svg";
import { TaxFieldActions } from "./components/TaxFieldActions";
import { TaxFieldDialog } from "./components/TaxFieldDialog";

type TaxFieldProps = {
  fields: InvoiceTaxRow[];
  onAdd: (field: InvoiceTaxRow) => void;
  onEdit: (field: InvoiceTaxRow) => void;
  onRemove: (id: string) => void;
  type: "settings" | "invoice";
  onToggle?: (id: string, isSelected: boolean) => void;
  selectedTaxes?: string[];
  max?: number;
  currency: string;
  title?: string;
};

export const TaxField = ({
  fields,
  onAdd,
  onEdit,
  onRemove,
  type,
  onToggle,
  selectedTaxes,
  max,
  currency,
  title,
}: TaxFieldProps) => {
  const { t } = useTranslations();

  const taxesLength = fields.length;

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const addField = (field: InvoiceTaxRow) => {
    onAdd(field);
  };

  const defaultColumn: TableGridColumn<InvoiceTaxRow> = {
    id: "default",
    title: "Default",
    width: 76,
    content: (row: InvoiceTaxRow) => (
      <div className="flex h-full items-center justify-center">
        <Checkbox
          isChecked={row.default}
          onChange={(e) => onToggle?.(row.id, e.target.checked)}
        />
      </div>
    ),
  };

  const selectedColumn: TableGridColumn<InvoiceTaxRow> = {
    id: "selected",
    title: "Selected",
    width: 76,
    content: (row: InvoiceTaxRow) => {
      const selectedTaxesInThisLevel = selectedTaxes?.filter((tax) =>
        Boolean(fields.find((field) => field.id === tax))
      );

      const isSelected = selectedTaxesInThisLevel?.includes(row.id);
      const selectedCount = selectedTaxesInThisLevel?.length ?? 0;
      const isDisabled =
        max !== undefined && selectedCount >= max && !isSelected;

      return (
        <div className="flex h-full items-center justify-center">
          <Checkbox
            isChecked={isSelected}
            onChange={(e) => onToggle?.(row.id, e.target.checked)}
            isDisabled={isDisabled}
          />
        </div>
      );
    },
  };

  const columns: TableGridColumn<InvoiceTaxRow>[] = [
    type === "settings" ? defaultColumn : selectedColumn,
    {
      id: "name",
      title: t({
        defaultMessage: "Name",
        id: "invoices.name",
      }),
      grow: true,
      content: ({ name, id, ...props }: InvoiceTaxRow) => (
        <div className="flex justify-between">
          <span className="truncate">{name}</span>

          {type === "invoice" && props.default && (
            <span className="text-xs text-grey-600">
              {t({
                defaultMessage: "default",
                id: "invoices.default",
              })}
            </span>
          )}
        </div>
      ),
    },
    {
      id: "percentage",
      title: t({
        defaultMessage: "Rate",
        id: "invoices.percentage",
      }),
      width: 150,
      content: ({ percentage }: InvoiceTaxRow) => (
        <div className="flex items-center gap-0.5">
          <span>{percentage}</span>
          <span className="text-xs text-[#A4A5AA]">%</span>
        </div>
      ),
    },
    {
      id: "action",
      title: t({
        defaultMessage: "Action",
        id: "common.action",
      }),
      headerClassName: "border-r-0",
      content: (row: InvoiceTaxRow) => (
        <TaxFieldActions
          item={row}
          onEdit={onEdit}
          onRemove={onRemove}
          currency={currency}
        />
      ),
    },
  ];

  return (
    <>
      {title && (
        <h2 className="mt-0 mb-8 font-secondary text-base font-medium">
          {title}
        </h2>
      )}

      {taxesLength ? (
        <TableGrid<InvoiceTaxRow>
          id="invoice-settings-form-tax-field"
          variant="grid"
          data={fields}
          classNames={{
            wrapper: "w-full",
          }}
          onReachEnd={() => undefined}
          columns={columns}
        />
      ) : null}

      <Button
        variant="tertiary"
        className="font-poppins mt-8 text-sm font-normal text-dark-600"
        onClick={() => setIsDialogOpen(true)}
        iconStart={<AddIcon />}
      >
        Add
      </Button>

      <TaxFieldDialog
        isDialogOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onSubmit={addField}
        currency={currency}
      />
    </>
  );
};
